import { isEmpty } from "lodash";
import { array, mixed, number, object, string } from "yup";
import { CampaignStepsUnion } from "./CampaignForm";

const triggersWithEvent = [
  "internal_event",
  "custom_event",
  "custom_event_unique_code",
];

export const getValidationSchemaByStep = (
  activeStep: CampaignStepsUnion,
  message: any,
  isEditMode: boolean,
) => {
  switch (activeStep) {
    case "trigger":
      return object().shape({
        trigger: string().required(message({ id: "requiredFieldError" })),
        event: string().when("trigger", {
          is: (trigger: string) => triggersWithEvent.includes(trigger),
          then: (schema) =>
            schema.required(message({ id: "requiredFieldError" })),
          otherwise: (schema) => schema.nullable(),
        }),
        achievementId: string().when("trigger", {
          is: (trigger: string) => trigger === "achievement",
          then: (schema) =>
            schema.required(message({ id: "requiredFieldError" })),
          otherwise: (schema) => schema.nullable(),
        }),
        eventCodeAttribute: string().when("trigger", {
          is: (trigger: string) => trigger === "custom_event_unique_code",
          then: (schema) =>
            schema.required(message({ id: "requiredFieldError" })),
          otherwise: (schema) => schema.nullable(),
        }),
        generateCodes: number().when("trigger", {
          is: (trigger: string) => trigger === "custom_event_unique_code",
          then: (schema) =>
            isEditMode
              ? schema.nullable()
              : schema.required(message({ id: "requiredFieldError" })),
          otherwise: (schema) => schema.nullable(),
        }),
        codeGenerator: mixed().when("trigger", {
          is: (trigger: string) => trigger === "custom_event_unique_code",
          then: () =>
            object().shape({
              characterSet: string()
                .nullable()
                .required(message({ id: "requiredFieldError" })),
              length: string()
                .nullable()
                .required(message({ id: "requiredFieldError" })),
            }),
          otherwise: () => mixed().nullable(),
        }),
      });
    case "info":
      return object().shape({
        translations: array().of(
          object().shape({
            name: string().required(message({ id: "requiredFieldError" })),
            description: string().nullable(),
          }),
        ),
        activity: object().shape({
          startsAt: string().required(message({ id: "requiredFieldError" })),
          endsAt: string().nullable(),
        }),
        labels: array().of(
          object().shape({
            key: string().required(message({ id: "requiredFieldError" })),
            value: string().required(message({ id: "requiredFieldError" })),
          }),
        ),
        visibility: object().shape({
          target: string().required(message({ id: "requiredFieldError" })),
          tiers: array()
            .of(string())
            .when("target", {
              is: "tier",
              then: (schema) =>
                schema
                  .min(1, message({ id: "requiredFieldError" }))
                  .required(message({ id: "requiredFieldError" })),
              otherwise: (schema) => schema.nullable(),
            }),
          segments: array()
            .of(string())
            .when("target", {
              is: "segment",
              then: (schema) =>
                schema
                  .min(1, message({ id: "requiredFieldError" }))
                  .required(message({ id: "requiredFieldError" })),
              otherwise: (schema) => schema.nullable(),
            }),
        }),
        displayOrder: number().nullable(),
      });
    case "rules":
      return object().shape({
        rules: array()
          .of(
            object().shape({
              effects: array()
                .min(1, message({ id: "requiredArray" }))
                .required(message({ id: "requiredArray" })),
            }),
          )
          .min(1, message({ id: "requiredArray" })),
      });
    case "limitation":
      return object().shape({
        limits: object().shape({
          executionsPerMember: object().shape({
            interval: object().shape({
              type: string(),
            }),
            value: number().when("interval.type", {
              is: (type: string) => type !== "noLimit",
              then: (schema) =>
                schema.required(message({ id: "requiredFieldError" })),
              otherwise: (schema) => schema.nullable(),
            }),
          }),
          points: object().shape({
            interval: object().shape({
              type: string(),
            }),
            value: number().when("interval.type", {
              is: (type: string) => type !== "noLimit",
              then: (schema) =>
                schema.required(message({ id: "requiredFieldError" })),
              otherwise: (schema) => schema.nullable(),
            }),
          }),
          pointsPerMember: object().shape({
            interval: object().shape({
              type: string(),
            }),
            value: number().when("interval.type", {
              is: (type: string) => type !== "noLimit",
              then: (schema) =>
                schema.required(message({ id: "requiredFieldError" })),
              otherwise: (schema) => schema.nullable(),
            }),
          }),
        }),
      });
  }
};

export const checkTranslationsError = (translations: any) => {
  if (!translations) return false;

  const areErrors = Object.keys(translations).some(
    (index) => !isEmpty(translations[index]),
  );
  return areErrors;
};

export const updateErrors = (
  formErrors: any,
  stepErrors: any,
  stepKeys: string[],
) => {
  let updatedErrors = { ...formErrors, ...stepErrors };

  stepKeys.forEach((key) => {
    if (stepErrors[key] === undefined) {
      delete updatedErrors[key];
    }
  });

  return updatedErrors;
};

export const hasRuleErrors = (errors: any): boolean => {
  if (!errors.rules) return false;

  return Object.values(errors.rules)?.some(
    (rule: any) =>
      Object.keys(rule?.effects || {}).length > 0 ||
      Object.keys(rule?.conditions || {}).length > 0,
  );
};
